import { MihatoMark } from "@/common/components/MihatoMark";
import { ApiError } from "@/common/lib/api";
import { useRequestLoginLink } from "@/features/auth/api/authApi";
import { readPendingInvitationToken, writePendingEmail } from "@/features/auth/lib/storage";
import { validateEmailOnly } from "@/features/auth/schemas";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const mutation = useRequestLoginLink();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const validationError = validateEmailOnly(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    const normalized = email.trim().toLowerCase();
    const invitationToken = readPendingInvitationToken();

    try {
      await mutation.mutateAsync({
        email: normalized,
        ...(invitationToken ? { invitationToken } : {}),
      });
      writePendingEmail(normalized);
      setSubmittedEmail(normalized);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        toast.error("Muchos intentos. Esperá unos minutos y volvé a probar.");
        return;
      }
      toast.error("No pudimos procesar tu pedido. Revisá tu conexión e intentá de nuevo.");
    }
  }

  return (
    <>
      <title>Ingresar · HATO</title>
      <meta name="robots" content="noindex, nofollow" />

      <div
        className="landing-texture pointer-events-none fixed inset-0 opacity-50"
        aria-hidden="true"
      />

      <main className="relative min-h-screen bg-brand-dark text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[90vh]"
          style={{
            background:
              "radial-gradient(circle at 50% 10%, rgba(0, 229, 153, 0.08) 0%, transparent 60%)",
          }}
        />

        <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
          <Link to="/" className="mb-10 flex items-center gap-3 self-start">
            <MihatoMark className="h-7 w-7 text-white" />
            <span className="font-medium text-lg tracking-tight">HATO</span>
          </Link>

          {submittedEmail ? (
            <output className="flex flex-col gap-4 rounded-2xl border border-primary-500/30 bg-primary-500/[0.06] p-6">
              <h1 className="font-medium text-2xl text-white tracking-tight md:text-3xl">
                Revisá tu email
              </h1>
              <p className="text-neutral-300 text-sm leading-relaxed">
                Si tu cuenta existe, te enviamos un email a <strong>{submittedEmail}</strong>. Abrí
                el link <strong>en el mismo navegador donde estás ahora</strong>.
              </p>
              <p className="text-neutral-400 text-xs leading-relaxed">
                ¿No llegó? Chequeá Promotions o Spam. Puede tardar un minuto.
              </p>
            </output>
          ) : (
            <>
              <div className="mb-8 flex flex-col gap-2">
                <h1 className="font-medium text-3xl text-white tracking-tight md:text-4xl">
                  Ingresar
                </h1>
                <p className="text-neutral-400 text-sm">
                  Ingresá tu correo y te enviamos un link mágico. Abrilo en el mismo navegador donde
                  estás ahora.
                </p>
              </div>

              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="font-medium text-neutral-300 text-sm">
                    Correo
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null);
                    }}
                    placeholder="tu@ejemplo.com"
                    aria-invalid={Boolean(error)}
                    className="h-12 w-full rounded-xl border border-white/[0.08] bg-brand-panel px-4 text-base text-white placeholder:text-neutral-500 focus:border-primary-500/60 focus:outline-none focus:ring-1 focus:ring-primary-500/40 disabled:opacity-60"
                    disabled={mutation.isPending}
                  />
                  {error && (
                    <p role="alert" className="text-rose-400 text-xs">
                      {error}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={mutation.isPending}
                  className="mt-2 flex h-12 items-center justify-center rounded-full bg-primary-500 px-6 font-medium text-brand-dark text-sm transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {mutation.isPending ? "Enviando…" : "Enviar link de acceso"}
                </button>
              </form>

              <p className="mt-6 text-center text-neutral-500 text-sm">
                ¿Todavía no tenés cuenta?{" "}
                <Link to="/access-request" className="text-primary-500 hover:text-primary-400">
                  Solicitá acceso
                </Link>
              </p>
            </>
          )}
        </div>
      </main>
    </>
  );
}
